import React, { Fragment, useState, useEffect } from "react";
import PropTypes from "prop-types";

import generateUUID from "uuid/v4";
import { styled } from "@mui/material/styles";

import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Clear";
import Button from "@mui/material/Button";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Paper from "@mui/material/Paper";

import usePrevious from "~/hooks/usePrevious";
import InlineToolbar from "~/components/InlineToolbar";

import { absolutePageToRelative } from "~/utils/page";
import { props } from "ramda";

const TabPaper = styled(Paper)(() => ({
  borderBottom: "1px solid #a5a2a2"
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  padding: theme.spacing(1),
  border: "1px solid #a5a2a2",
  borderRadius: "4px",
  height: "100%",
  overflow: "hidden",
  "&.MuiTextField-root textarea:focus-visible": {
    border: theme.outline()
    // outlineOffset: "-2px"
  }
}));

const Content = styled(DialogContent)(({ theme }) => ({
  padding: `${theme.spacing(2)} !important`
}));

const Title = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  marginLeft: 0,
  marginRight: "auto"
}));

const TitleWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  paddingTop: theme.spacing(1)
}));

const Header = styled(DialogTitle)(() => ({
  position: "relative",
  margin: "0 0 16px",
  padding: 0,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between"
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  "&:focus-visible": {
    outline: theme.outline(),
    outlineOffset: "-2px"
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  "&:focus-visible": {
    outline: theme.outline(),
    outlineOffset: "-2px"
  }
}));

const getOutlinedTextfieldProps = () => ({
  disableUnderline: true,
  style: { overflowY: "auto" }
});

const getStandardTextfieldProps = isSinglePage => ({
  cols: 100,
  // rows: isSinglePage ? 11 : 8,
  style: { alignSelf: "start" }
});

const StyledTab = styled(Tab)(({ theme }) => ({
  "&:focus-visible": {
    outline: theme.outline(),
    outlineOffset: "-2px"
  }
}));

function NotesModal({
  showModal,
  hideModal,
  note,
  updateNote,
  material,
  addNote,
  closeDialog,
  noteId,
  displayedPages,
  absolutePage,
  bookTitle,
  kind
}) {
  const [text, setText] = useState(note ? note.text : "");
  const [tabValue, setTabValue] = useState(0);
  const prevProps = usePrevious({ note, noteId, material });

  useEffect(() => {
    setText(note ? note.text : "");
  }, [note]);

  // make sure to reload correct page after changing page mode while notes modal is open
  useEffect(() => {
    save(note, text, noteId, material);
    if (!material && showModal) {
      const newRelativePage = absolutePageToRelative(absolutePage);
      showModal("NOTES", {
        noteId: `${bookTitle}@page_${newRelativePage}`,
        material
      });
    }
    setTabValue(displayedPages.indexOf(absolutePage));
  }, [displayedPages, absolutePage]);

  useEffect(() => {
    if (!prevProps) return;
    const { note, noteId, material } = prevProps;
    if (note && note.text !== text) {
      save(note, text, noteId, material);
    }
  }, [material]);

  function save(note, text, noteId, material) {
    if (note) {
      updateNote({ ...note, text });
    } else {
      addNote({
        _id: noteId || generateUUID(),
        type: "note",
        materialId: material ? material._id : null,
        text
      });
    }
  }

  function onSaveClick() {
    save(note, text, noteId, material);
    if (hideModal) hideModal();
    if (closeDialog) closeDialog();
  }

  function onChange(event) {
    setText(event.target.value);
  }

  function onTabChange(event, newTabValue) {
    save(note, text, noteId, material);
    const newRelativePage = absolutePageToRelative(displayedPages[newTabValue]);
    setTabValue(newTabValue);
    showModal("NOTES", {
      noteId: `${bookTitle}@page_${newRelativePage}`,
      material
    });
  }

  function getTitle() {
    if (material) {
      return `Material: ${material.title || material.name}`;
    }
    return `${bookTitle} Seite ${absolutePageToRelative(displayedPages[tabValue])}`;
  }

  function printNotes() {
    window.issueNotesPrint(getTitle(), text);
  }

  function isSinglePage() {
    return material || displayedPages.length === 1;
  }

  return (
    <>
      <Header className="notes-dialog-title">
        <TitleWrapper>
          <Title id={`${kind}-modal-title`} variant="h6">
            Notizen
          </Title>
          <Title variant="subtitle1">{getTitle()}</Title>
        </TitleWrapper>

        <InlineToolbar>
          <StyledIconButton onClick={onSaveClick} size="large" aria-label="Speichern und schließen">
            <CloseIcon />
          </StyledIconButton>
        </InlineToolbar>
      </Header>
      {!isSinglePage() && (
        <TabPaper elevation="0" square>
          {/* FIXME: textColor="inherit" may not be working */}
          <Tabs
            value={tabValue}
            onChange={onTabChange}
            indicatorColor="primary"
            textColor="inherit"
            onKeyDown={event => event.stopPropagation()}
          >
            {displayedPages.map(page => (
              <StyledTab key={page} label={`Seite ${absolutePageToRelative(page)}`} />
            ))}
          </Tabs>
        </TabPaper>
      )}
      <Content>
        <StyledTextField
          value={text}
          onChange={onChange}
          variant="standard"
          placeholder="Notiz..."
          multiline
          fullWidth
          slotProps={{
            input: getOutlinedTextfieldProps(),
            htmlInput: getStandardTextfieldProps(isSinglePage())
          }}
          rows={isSinglePage() ? 11 : 8}
          autoFocus
        />
      </Content>
      <DialogActions>
        <StyledButton variant="text" color="primary" onClick={printNotes}>
          Drucken
        </StyledButton>
        <StyledButton variant="text" color="primary" onClick={onSaveClick}>
          Speichern
        </StyledButton>
      </DialogActions>
    </>
  );
}

NotesModal.propTypes = {
  showModal: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired,
  updateNote: PropTypes.func.isRequired,
  addNote: PropTypes.func.isRequired,
  material: PropTypes.shape({}),
  note: PropTypes.object,
  closeDialog: PropTypes.func.isRequired,
  noteId: PropTypes.string.isRequired,
  displayedPages: PropTypes.array.isRequired,
  absolutePage: PropTypes.number.isRequired,
  bookTitle: PropTypes.string.isRequired,
  kind: PropTypes.string.isRequired
};

export default NotesModal;
